import React, { useState, useEffect, useCallback } from "react";
import { Modal } from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import { useWalletConnection } from "../../../hooks/useWalletConnection";
import { getQueryableAddress, executePerps } from "../../functions/Contract";
import { getMarketName } from "../../../utils/marketUtils";
import { useMarketPrice } from "../../../hooks/useMarketPrices";
import { closeTradeMarketEvm } from "../../../evm/services/tradingEvmService";
import { getNibiruSDK, getContractAddresses } from "../../../config/network";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import UpdateActionsModal from "./UpdateActionsModal";
import { GreenButton } from "../../Buttons/GreenButton";
import { RedButton } from "../../Buttons/RedButton";
import { formatWithSubscript } from "../../utils/formatter";

const nibiru = getNibiruSDK();
const GRAPHQL_URL = "https://sai-keeper.nibiru.fi/query";

// ─── GraphQL ───
const TRADE_DETAIL_QUERY = `
  query GetTradeDetail($trader: String!, $id: Int!, $marketId: Int!, $collateralId: Int!) {
    perp {
      trade(trader: $trader, id: $id) {
        id trader isOpen isLong leverage collateralAmount openCollateralAmount
        openPrice closePrice sl tp tradeType
        state {
          pnlCollateral pnlPct pnlCollateralAfterFees positionValue
          liquidationPrice borrowingFeeCollateral borrowingFeePct
          closingFeeCollateral closingFeePct remainingCollateralAfterFees
        }
      }
      borrowing(marketId: $marketId, collateralId: $collateralId) {
        openFeePct closeFeePct triggerOrderFeePct
        feesPerHourLong feesPerHourShort oiLong oiShort oiMax
      }
    }
  }
`;
const TRADE_HISTORY_QUERY = `
  query GetTradeHistory($trader: String!) {
    perp {
      tradeHistory(where: { trader: $trader } limit: 500 order_by: sequence order_desc: true) {
        id tradeChangeType realizedPnlCollateral realizedPnlPct
        trade { id }
      }
    }
  }
`;

const gqlFetch = async (query, variables) => {
  const r = await fetch(GRAPHQL_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ query, variables }),
  });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const j = await r.json();
  if (j.errors) throw new Error(j.errors[0]?.message || "GraphQL error");
  return j.data;
};

const fetchCloseEvent = async (trader, tradeId) => {
  const d = await gqlFetch(TRADE_HISTORY_QUERY, { trader });
  const h = d?.perp?.tradeHistory || [];
  const CT = new Set(["position_closed_user","position_closed_sl","position_closed_tp","position_liquidated"]);
  return h.find((x) => x.trade?.id === tradeId && CT.has(x.tradeChangeType)) || null;
};

const parseIdx = (v) => {
  if (v == null) return null;
  if (typeof v === "number") return v;
  const m = String(v).match(/\((\d+)\)/);
  return m ? parseInt(m[1], 10) : parseInt(v, 10);
};

// ─── Formatters ───
const fUsd = (v) => {
  if (v == null) return "-";
  const n = typeof v === "string" ? parseFloat(v) : v;
  if (isNaN(n)) return "-";
  const a = Math.abs(n);
  let d = 2;
  if (a > 0 && a < 0.01) { while (d < 8 && Number(a.toFixed(d)) === 0) d++; d = Math.min(d + 2, 8); }
  return "$" + a.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
};
const fPrice = (v) => {
  if (v == null || v === "" || v === "0") return "-";
  const n = parseFloat(v);
  if (isNaN(n) || n === 0) return "-";
  const a = Math.abs(n);
  let d = 2; if (a < 1) { d = 6; if (a < 0.0001) d = 8; }
  return "$" + n.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
};
const fPct = (v) => {
  if (v == null) return null;
  const n = typeof v === "string" ? parseFloat(v) : v;
  if (isNaN(n)) return null;
  return Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 4 }) + "%";
};

// SubZero removed — using formatWithSubscript from utils/formatter

// ─── Skeleton ───
const Skel = () => (
  <div className="flex justify-between items-center py-2">
    <div className="animate-pulse h-3.5 w-20 rounded" style={{ background: "var(--surface-3)" }} />
    <div className="animate-pulse h-3.5 w-16 rounded" style={{ background: "var(--surface-3)" }} />
  </div>
);

// Google Fonts link for Inter (inject once)
if (typeof document !== "undefined" && !document.getElementById("inter-font-link")) {
  const link = document.createElement("link");
  link.id = "inter-font-link";
  link.rel = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap";
  document.head.appendChild(link);
}
const FONT = "'Inter', 'General Sans', -apple-system, sans-serif";

// ─── Component ───
const TradeDetailModal = ({ isOpen, onClose, trade }) => {
  const { account, walletType, isEvm } = useWalletConnection();
  const dispatch = useDispatch();
  const [tradeState, setTradeState] = useState(null);
  const [borrowingData, setBorrowingData] = useState(null);
  const [closeEvent, setCloseEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const { price: livePrice } = useMarketPrice(
    trade?.is_open && trade?.market_index != null ? trade.market_index : null, 30000
  );

  const fetchData = useCallback(async () => {
    if (!trade || !account) return;
    setLoading(true); setError(null);
    try {
      const tradeId = parseIdx(trade.user_trade_index);
      if (tradeId === null) throw new Error("Invalid trade index");
      const bech32 = await getQueryableAddress(account, walletType);
      if (!bech32) throw new Error("Could not resolve address");
      const raw = process.env.REACT_APP_PERP_TOKEN_INDEXES_COLLATERAL || "1";
      const cm = raw.match(/\((\d+)\)/);
      const collateralId = cm ? parseInt(cm[1], 10) : parseInt(raw, 10);
      const marketId = parseIdx(trade.market_index);
      if (isNaN(marketId)) throw new Error("Bad market index: " + trade.market_index);

      const data = await gqlFetch(TRADE_DETAIL_QUERY, { trader: bech32, id: tradeId, marketId, collateralId });
      setTradeState(data?.perp?.trade || null);
      setBorrowingData(data?.perp?.borrowing || null);
      if (!trade.is_open) {
        const ev = await fetchCloseEvent(bech32, tradeId);
        if (ev) setCloseEvent(ev);
      }
    } catch (e) { console.error("[TradeDetail]", e); setError(e.message); }
    finally { setLoading(false); }
  }, [trade, account, walletType]);

  useEffect(() => {
    if (isOpen && trade) fetchData();
    else { setTradeState(null); setBorrowingData(null); setCloseEvent(null); setLoading(true); setError(null); }
  }, [isOpen, trade, fetchData]);

  // ─── Close trade handler ───
  const handleCloseTrade = async () => {
    if (!account) { toast.error("Connect your wallet first"); return; }
    onClose(); // close detail modal immediately
    try {
      if (isEvm) {
        const idx = parseIdx(trade.user_trade_index);
        if (idx === null) { toast.error("Invalid trade ID"); return; }
        const res = await closeTradeMarketEvm(idx, true, dispatch);
        if (!res.success) throw new Error(res.error || "Close failed");
      } else {
        const res = await executePerps(nibiru.chainId, nibiru.endptTm, getContractAddresses().perp, dispatch,
          { close_trade: { trade_index: trade.user_trade_index } }, account, walletType);
        if (!res) throw new Error("Transaction failed");
      }
      window.dispatchEvent(new CustomEvent('userTradeAction', { detail: { action: 'close_trade', tradeIndex: trade.user_trade_index, timestamp: Date.now() } }));
      setTimeout(() => window.dispatchEvent(new CustomEvent('blockchainStateCheck', { detail: { account, timestamp: Date.now() } })), 3000);
    } catch (e) { toast.error(`Close failed: ${e.message}`); }
  };

  if (!trade) return null;

  // ─── Derived ───
  const market = getMarketName(trade.market_index);
  // collateral_amount from contract is AFTER open fee was deducted
  const margin = parseFloat(trade.collateral_amount) / 1e6; // post-fee collateral
  const lev = parseFloat(trade.leverage);
  const size = margin * lev; // current position size (post-fee)
  const entry = parseFloat(trade.open_price);
  const isLong = trade.long;
  const isLimit = trade.trade_type === "limit";
  const st = tradeState?.state;
  const curPrice = trade.is_open ? livePrice || (st ? entry : null) : parseFloat(trade.trade_info?.closed_price || trade.close_price || "0");

  // PnL
  let pnl = null, pnlP = null, profit = false;
  if (st) { pnl = st.pnlCollateral / 1e6; pnlP = st.pnlPct; profit = pnl >= 0; }
  else if (entry && curPrice) {
    const q = size / entry;
    pnl = isLong ? (curPrice - entry) * q : (entry - curPrice) * q;
    pnlP = (pnl / margin) * 100; profit = pnl >= 0;
  }

  // Fees
  let openFee = null, openPct = null, closeFee = null, closePct = null;
  let borrowFee = null, borrowPctR = null, pnlNet = null, remain = null;
  let originalDeposit = null; // what the user actually sent before open fee

  if (st) {
    borrowFee = st.borrowingFeeCollateral / 1e6; borrowPctR = st.borrowingFeePct;
    closeFee = st.closingFeeCollateral / 1e6; closePct = st.closingFeePct;
    pnlNet = st.pnlCollateralAfterFees / 1e6; remain = st.remainingCollateralAfterFees / 1e6;
  }
  // Use openCollateralAmount from indexer when available (original deposit before open fee)
  const openCollateral = tradeState?.openCollateralAmount ? tradeState.openCollateralAmount / 1e6 : null;
  if (openCollateral && openCollateral > margin) {
    originalDeposit = openCollateral;
    openFee = openCollateral - margin; // exact open fee = deposit - post-fee collateral
    // Derive openPct from the actual fee: fee = originalSize * pct → pct = fee / (originalDeposit * lev)
    if (originalDeposit * lev > 0) openPct = openFee / (originalDeposit * lev);
  }
  if (borrowingData) {
    const op = isLimit ? (borrowingData.triggerOrderFeePct || borrowingData.openFeePct) : borrowingData.openFeePct;
    // Only use borrowingData for open fee if we didn't get it from openCollateralAmount
    if (openFee === null) {
      openPct = op;
      if (trade.is_open) {
        const divisor = 1 - lev * op;
        if (divisor > 0) {
          originalDeposit = margin / divisor;
          openFee = originalDeposit * lev * op;
        } else {
          openFee = size * op;
        }
      } else {
        openFee = size * op;
      }
    }
    if (closeFee === null) { closePct = borrowingData.closeFeePct; closeFee = size * borrowingData.closeFeePct; }
  }
  if (borrowFee === null && !trade.is_open && closeEvent && pnl !== null) {
    // Try to derive borrowFee from history. realizedPnlCollateral may be gross or net —
    // if derivation gives negative, borrow fee was ~0 for this trade.
    const rp = closeEvent.realizedPnlCollateral / 1e6;
    const derived = pnl - (closeFee || 0) - rp;
    borrowFee = derived > 0.0001 ? derived : 0; // only use if meaningfully positive
    if (margin > 0 && borrowFee > 0) borrowPctR = (borrowFee / margin) * 100;
    // Do NOT set pnlNet from rp — compute it below from grossPnl - exit fees
  }
  const liqPrice = st?.liquidationPrice ?? trade.trade_info?.liquidation_price ?? trade.liquidation_price ?? null;
  // Net PnL = gross price PnL minus fees deducted at close (close + borrow)
  // Open fee is NOT subtracted here — it was already taken from collateral at entry
  if (pnlNet === null && pnl !== null) pnlNet = pnl - (closeFee || 0) - (borrowFee || 0);

  const totalFee = (openFee || 0) + (closeFee || 0) + (borrowFee || 0);
  const exitFee = (closeFee || 0) + (borrowFee || 0);
  const pc = profit ? "#30AA67" : "#E4474A";
  const nc = pnlNet != null ? (pnlNet >= 0 ? "#30AA67" : "#E4474A") : "var(--ink)";
  // The hero sits on a tinted panel, so it reads the on-tint pair instead.
  const heroC = pnlNet != null ? (pnlNet >= 0 ? "var(--on-tint-up)" : "var(--on-tint-down)") : "var(--ink)";
  const netPct = (pnlNet != null && margin > 0) ? (pnlNet / margin) * 100 : null;
  const ns = pnlNet != null ? (pnlNet >= 0 ? "+" : "-") : "";
  const na = pnlNet != null ? fUsd(Math.abs(pnlNet)) : "-";
  const fr = borrowingData ? (isLong ? borrowingData.feesPerHourLong : borrowingData.feesPerHourShort) : null;
  const oiL = borrowingData?.oiLong ?? null;
  const oiS = borrowingData?.oiShort ?? null;

  const fv = (a) => a != null ? <span style={{ color: "#E4474A" }}>-{fUsd(a)}</span> : "-";
  const fs = (p, b) => { if (p == null) return null; const d = fPct(p * 100); return d ? `${d} of ${b}` : null; };

  // ─── Tooltip ───
  const Tip = ({ text, children }) => (
    <span className="relative group/tip cursor-help inline-flex items-center gap-0.5">
      {children}
      <svg className="w-[13px] h-[13px] td-tip-ico transition-colors shrink-0" viewBox="0 0 16 16" fill="currentColor">
        <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 2.5a1 1 0 110 2 1 1 0 010-2zM6.75 7h1.5v4.5h-1.5V7z"/>
      </svg>
      <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 opacity-0 group-hover/tip:opacity-100 transition-opacity z-50" style={{ width: 210 }}>
        <span className="block rounded-[10px] text-[11.5px] leading-[1.5] px-3 py-2" style={{ background: "var(--surface)", color: "var(--ink-2)", fontFamily: FONT, fontWeight: 500, boxShadow: "0 4px 20px rgba(0,0,0,0.25)", border: "1px solid var(--line-2)" }}>
          {text}
        </span>
        <span className="block mx-auto" style={{ width: 0, height: 0, borderLeft: "5px solid transparent", borderRight: "5px solid transparent", borderTop: "5px solid var(--surface)" }} />
      </span>
    </span>
  );

  // ─── Row ───
  const R = ({ label, tip, value, sub, bold, line }) => (
    <div className={`flex justify-between items-center py-[5px] ${line ? "border-t mt-1 pt-[7px]" : ""}`} style={line ? { borderColor: "var(--line-2)" } : undefined}>
      <span className="text-[12.5px]" style={{ color: "var(--ink-2)", fontFamily: FONT }}>{tip ? <Tip text={tip}>{label}</Tip> : label}</span>
      <div className="flex flex-col items-end">
        <span className={`text-[12.5px] ${bold ? "font-bold" : "font-semibold"}`} style={{ color: "var(--ink)", fontFamily: FONT }}>{value}</span>
        {sub && <span className="text-[10.5px]" style={{ color: "var(--ink-3)", fontFamily: FONT }}>{sub}</span>}
      </div>
    </div>
  );

  return (
    <>
      <Modal open={isOpen} onClose={onClose} center closeOnOverlayClick closeOnEsc
        classNames={{ modal: "max-sm:min-w-[95vw]" }}
        /* Radius, fill, shadow and the entry animation come from
           `neumorph2026.css` §8, which styles every modal in the app as one
           object. Only the width is this modal's own. */
        styles={{ modal: { padding: 0, width: 700, maxWidth: "95vw", overflow: "visible" } }}
      >
        {/* Header */}
        <div className="px-5 pt-4 pb-3 border-b flex items-center justify-between" style={{ borderColor: "var(--line-2)" }}>
          <div className="flex items-center gap-2">
            <span className="text-[15px] font-bold" style={{ color: "var(--ink)", fontFamily: FONT }}>{market}</span>
            <span className="text-[10.5px] font-bold px-2 py-[2px] rounded-full"
              style={{ background: isLong ? "var(--tint-up)" : "var(--tint-down)", color: isLong ? "var(--on-tint-up)" : "var(--on-tint-down)" }}>
              {isLong ? "LONG" : "SHORT"} {lev.toFixed(0)}x
            </span>
            <span className="text-[10.5px] font-semibold px-2 py-[2px] rounded-full"
              style={{ background: trade.is_open ? "var(--tint-up)" : "var(--surface-3)", color: trade.is_open ? "var(--on-tint-up)" : "var(--ink-4)" }}>
              {trade.is_open ? "OPEN" : "CLOSED"}
            </span>
          </div>
          {/* Action buttons in header for open trades */}
          {!loading && !error && trade.is_open && (
            <div className="flex items-center gap-2 mr-8">
              <div onClick={() => { onClose(); setTimeout(() => setEditModalOpen(true), 150); }}>
                <GreenButton text="Edit" className="!text-[12px] !px-4 !py-[5px] !rounded-[10px]" />
              </div>
              <div onClick={handleCloseTrade}>
                <RedButton text="Close" className="!text-[12px] !px-4 !py-[5px] !rounded-[10px]" />
              </div>
            </div>
          )}
        </div>

        {/* Body */}
        <div style={{ fontFamily: FONT }}>
          {loading ? (
            <div className="px-5 py-5 space-y-1">{Array.from({ length: 6 }).map((_, i) => <Skel key={i} />)}</div>
          ) : error ? (
            <div className="py-8 text-center">
              <p className="text-[13px] text-[#E4474A] mb-2">Failed to load details</p>
              <button onClick={fetchData} className="text-[13px] text-[#7C2BE8] font-semibold hover:underline cursor-pointer">Retry</button>
            </div>
          ) : (
            <>
              {/* Hero */}
              <div className="mx-5 mt-3.5 mb-3 rounded-xl px-4 py-3 flex items-center justify-between"
                style={{ background: pnlNet != null ? (pnlNet >= 0 ? "var(--tint-up)" : "var(--tint-down)") : "var(--surface-accent)" }}>
                <div>
                  <p className="text-[10.5px] font-medium mb-0.5" style={{ color: "var(--ink-2)", fontFamily: FONT }}>
                    {trade.is_open ? "Unrealized P&L (after fees)" : "Realized P&L (after fees)"}
                  </p>
                  <p className="text-[20px] font-bold leading-tight" style={{ color: heroC, fontFamily: FONT }}>{ns}{na}</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  {netPct != null && (
                    <div className="text-[12px] font-bold px-2.5 py-[3px] rounded-lg"
                      style={{ background: pnlNet >= 0 ? "rgba(48,170,103,.18)" : "rgba(228,71,74,.18)", color: heroC }}>
                      {pnlNet >= 0 ? "+" : "-"}{fPct(netPct)}
                    </div>
                  )}
                  {remain != null && trade.is_open && (
                    <span className="text-[10.5px]" style={{ color: "var(--ink-3)" }}>{fUsd(remain)} left of {fUsd(margin)}</span>
                  )}
                </div>
              </div>

              {/* 2-col grid */}
              <div className="px-5 pb-4 grid grid-cols-2 gap-x-6">
                {/* Left: Position */}
                <div>
                  <p className="text-[10.5px] font-bold uppercase tracking-wider pb-1 mb-0.5 border-b" style={{ borderColor: "var(--line-2)", color: "#7C2BE8" }}>Position</p>
                  <R
                    label="Collateral"
                    tip={originalDeposit
                      ? `You deposited ${fUsd(originalDeposit)}. After the open fee (${fUsd(openFee)}), ${fUsd(margin)} is held as margin.`
                      : "Your margin after the open fee was deducted from your deposit."
                    }
                    value={fUsd(margin)}
                    sub={originalDeposit ? `deposited ${fUsd(originalDeposit)}` : "after open fee"}
                  />
                  <R label="Size" tip={`Collateral x leverage: ${fUsd(margin)} x ${lev.toFixed(0)}`} value={fUsd(size)} sub={`${lev.toFixed(0)}x leverage`} />
                  <R label="Entry" value={fPrice(entry)} />
                  <R label={trade.is_open ? "Now" : "Exit"} value={fPrice(curPrice)} />
                  <R label="Liquidation" tip="Your position auto-closes at this price to prevent further losses." value={liqPrice ? <span style={{ color: "#E4474A" }}>{fPrice(liqPrice)}</span> : "-"} />
                  {trade.tp && parseFloat(trade.tp) !== 0 && <R label="Take Profit" tip="Auto-closes at this price to lock in gains." value={<span style={{ color: "#30AA67" }}>{fPrice(trade.tp)}</span>} />}
                  {trade.sl && parseFloat(trade.sl) !== 0 && <R label="Stop Loss" tip="Auto-closes at this price to limit losses." value={<span style={{ color: "#E4474A" }}>{fPrice(trade.sl)}</span>} />}
                </div>

                {/* Right: Fees & Math */}
                <div>
                  <p className="text-[10.5px] font-bold uppercase tracking-wider pb-1 mb-0.5 border-b" style={{ borderColor: "var(--line-2)", color: "#7C2BE8" }}>Costs & Returns</p>
                  <R label={isLimit ? "Trigger Fee" : "Open Fee"} tip={isLimit ? "Fee for executing your limit order. Already taken from your collateral when you opened." : "One-time fee taken from your collateral when you opened. Based on position size."} value={fv(openFee)} sub={fs(openPct, "size")} />
                  <R label="Close Fee" tip="Taken from your P&L when the trade closes, along with the borrow fee. Based on position size." value={fv(closeFee)} sub={fs(closePct, "size")} />
                  <R label="Borrow Fee" tip="Grows over time while your position is open. The more the market leans one way (longs vs shorts), the higher this gets." value={fv(borrowFee)} sub={borrowPctR != null ? `${fPct(borrowPctR)} of collateral` : null} />
                  {/* Funding rate pills hidden for now
                  {trade.is_open && fr != null && (
                    <div className="flex items-center flex-wrap gap-1.5 mt-0.5 mb-0.5">
                      <Tip text="Current hourly borrow rate for your side. Changes with market balance.">
                        <span className="text-[10px] px-1.5 py-[1px] rounded-full font-semibold" style={{ background: "var(--surface-accent)", color: "#7C2BE8", border: "1px solid var(--line-accent)" }}>
                          {formatWithSubscript(fr * 100, { suffix: "%" })}/hr
                        </span>
                      </Tip>
                      {oiL != null && oiS != null && (
                        <Tip text={`Longs: ${fUsd(oiL / 1e6)} vs Shorts: ${fUsd(oiS / 1e6)}. The bigger side pays more.`}>
                          <span className="text-[10px] px-1.5 py-[1px] rounded-full font-semibold" style={{ background: "var(--surface-accent)", color: "#7C2BE8", border: "1px solid var(--line-accent)" }}>
                            L {fUsd(oiL / 1e6)} / S {fUsd(oiS / 1e6)}
                          </span>
                        </Tip>
                      )}
                    </div>
                  )}
                  */}
                  <R label="Total Fees" value={<span style={{ color: "#E4474A" }}>-{fUsd(totalFee)}</span>} bold line />

                  {/* Math */}
                  <div className="mt-1.5">
                    <R label="Price P&L" tip="Profit or loss from price movement alone, before fees." value={pnl != null ? <span style={{ color: pc }}>{profit ? "+" : "-"}{fUsd(Math.abs(pnl))}</span> : "-"} />
                    <R label="- Close & Borrow" tip="These fees are taken from your P&L when the trade settles. Open fee was already paid upfront from collateral." value={<span style={{ color: "#E4474A" }}>-{fUsd(exitFee)}</span>} />
                    <R label={trade.is_open ? "= Net P&L" : "= Final P&L"} tip={trade.is_open ? "What you'd get if you closed now." : "Your final profit or loss after close & borrow fees."} value={pnlNet != null ? <span className="text-[14px] font-bold" style={{ color: nc }}>{ns}{na}</span> : "-"} bold line />
                    {!trade.is_open && pnlNet != null && (
                      <R
                        label="Returned to wallet"
                        tip={`Your collateral (${fUsd(margin)}) plus your net P&L (${ns}${na}). This is the total amount sent back to your wallet.`}
                        value={<span className="text-[13px] font-bold" style={{ color: "var(--ink)" }}>{fUsd(margin + pnlNet)}</span>}
                        line
                      />
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Edit modal (rendered outside) */}
      {trade.is_open && (
        <UpdateActionsModal isOpen={editModalOpen} setIsOpen={setEditModalOpen} trade={trade} />
      )}
    </>
  );
};

export default TradeDetailModal;
